<script>
import { defineComponent } from 'vue';
import { mapActions } from 'vuex';
import { globalVar } from '@/helpers/globals';
import { Event } from '@/models/Event';
import SimpleImageDetailDlg from '@/components/event/SimpleImageDetailDlg.vue';
import MMSelectDialog from '@/components/media_manager/SelectFormatDialog.vue';
import { errToStr } from '@/helpers/tools';
import ImageUploadCard from '@/components/ImageUploadCard.vue';
import { Image } from '@/models/Image';
import { commitToast } from '@/plugins/toast';

export default defineComponent({
    name: 'CompactViewImagePanel',
    components: {
        SimpleImageDetailDlg,
        MMSelectDialog,
        ImageUploadCard,
    },
    props: {
        eventRecord: {
            type: Event,
            required: true,
        },
    },
    data() {
        return {
            showSimpleDetailDlg: false,
            showImgCropDlgOnOpen: false,
            imgSelectDlg: false,
            initialImage: null,
            cacheBuster: new Date().getTime(),
        };
    },
    computed: {
        featured_image_id() {
            return this.eventRecord.featured_image_id;
        },
        imageRec() {
            return this.$store.getters['image/imageById'](this.featured_image_id);
        },
    },
    watch: {
        featured_image_id() {
            this.loadImage({ imageId: this.featured_image_id });
        },
    },
    created() {
        // static view variables:
        this.globalVar = globalVar;
        this.imageConfig = this.globalVar('featured_images_config') || { event: {} };
        this.thumbImageConfig = this.imageConfig.event.front_50 || this.imageConfig.event.thumb;
    },
    mounted() {
        this.loadImage({ imageId: this.featured_image_id });
    },
    methods: {
        ...mapActions('image', ['loadImage']),
        getImageUrl(eventRec, imageConf) {
            return `${this.globalVar('imageroot')}/event/${eventRec.id}/${imageConf.ident}.${imageConf.format}?ts=${
                this.cacheBuster
            }`;
        },
        /**
         * After the user edited the image record's metadata,
         * we take the caption text and store it direcly on the event,
         * for de-coupling it from the original image.
         */
        async onImageEdit() {
            this.showSimpleDetailDlg = false;
            if (!this.featured_image_id) {
                return;
            }
            try {
                // emit record change event:
                await this.imageRec.save();
                this.$emit('propertyChange', { property: 'featrued_image_caption', value: this.imageRec.caption });
                this.cacheBuster = new Date().getTime();
                this.$emit('featured-image-created');
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            }
        },

        async onImageSelect(image) {
            if (image) {
                await this.onImageCrop({}, image);
            }
        },

        async onImageCrop(cropData, image) {
            if (image) {
                try {
                    this.imgSelectDlg = false;
                    await this.createFeaturedImage(cropData, image, this.eventRecord);
                    const eventChanges = [
                        { property: 'featured_image_id', value: image.id },
                        { property: 'featured_img_crop_config', value: cropData || null },
                    ];
                    this.$emit('propertyChange', eventChanges);

                    this.cacheBuster = new Date().getTime();
                    this.loadImage({ imageId: image.id });
                } catch (e) {
                    commitToast({ message: errToStr(e), color: 'error' });
                }
            }
        },

        async afterImageUploaded(images) {
            if (images?.length) {
                const img = new Image(images[0]);
                await this.onImageCrop(null, img);
            }
        },

        async createFeaturedImage(cropData, image, eventRec) {
            await image.createFeaturedImage('event', cropData, eventRec.id);
        },

        async deleteImg() {
            if (!this.featured_image_id) {
                return;
            }
            try {
                this.$emit('propertyChange', [
                    { property: 'featured_image_id', value: null },
                    { property: 'featured_img_crop_config', value: null },
                ]);
                this.cacheBuster = new Date().getTime();
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            }
        },

        downloadImg() {
            if (!this.imageRec || !this.imageRec.url) {
                return;
            }
            window.open(this.imageRec.url, '_blank');
        },
    },
});
</script>

<template>
    <div>
        <h3 class="section-heading">{{ $t('titelbild') }}</h3>
        <v-img
            v-if="imageRec"
            class="my-3"
            :src="getImageUrl(eventRecord, thumbImageConfig)"
            :alt="thumbImageConfig.text"
            :title="thumbImageConfig.text"
        >
            <!-- delete img btn -->
            <ActionBtn class="delImgBtn" color="red white--text" @click.stop="deleteImg">
                <KpIcon icon-cls="trash" color="white" disabled-color="grey" />
            </ActionBtn>
            <!-- download img btn -->
            <ActionBtn class="downloadImgBtn" @click.stop="downloadImg">
                <KpIcon icon-cls="download" color="white" disabled-color="grey" />
            </ActionBtn>
        </v-img>

        <!-- Choose Image drag panel -->
        <h4>{{ $t('bild_ersetzen') }}</h4>
        <ImageUploadCard
            width="100%"
            height="100%"
            :upload-params="{ event_organizer_id: eventRecord.organizer_id }"
            @uploaded="afterImageUploaded"
        />

        <!-- Edit buttons -->
        <div class="d-flex flex-wrap gap-3 mt-2">
            <!-- crop img btn -->
            <ActionBtn
                @click.stop="
                    initialImage = imageRec;
                    showImgCropDlgOnOpen = true;
                    imgSelectDlg = true;
                "
            >
                <KpIcon icon-cls="crop" color="white" disabled-color="grey" left />
                {{ $t('edit') }}
            </ActionBtn>
            <!-- change img infos btn -->
            <ActionBtn @click.stop="showSimpleDetailDlg = true">
                <KpIcon icon-cls="pencil" color="white" disabled-color="grey" left />
                {{ $t('infos') }}
            </ActionBtn>
            <!-- choose img btn -->
            <ActionBtn
                @click.stop="
                    initialImage = null;
                    showImgCropDlgOnOpen = false;
                    imgSelectDlg = true;
                "
            >
                <KpIcon icon-cls="mediamanager" color="white" disabled-color="grey" left />
                {{ $t('ersetzen') }}
            </ActionBtn>
        </div>

        <SimpleImageDetailDlg
            v-if="imageRec"
            v-model="showSimpleDetailDlg"
            :image-record="imageRec"
            @save="onImageEdit"
        />
        <MMSelectDialog
            v-if="imgSelectDlg"
            v-model="imgSelectDlg"
            :image-formats-config="imageConfig.event"
            :image-crop-data="eventRecord.featured_img_crop_config"
            :initial-image="initialImage"
            :initial-step="showImgCropDlgOnOpen ? 2 : 1"
            :organizer-id="eventRecord.organizer_id"
            @image-select="onImageSelect"
            @image-crop="onImageCrop"
        />
    </div>
</template>

<style scoped lang="scss">
.delImgBtn {
    position: absolute;
    bottom: 5px;
    left: 5px;
}
.downloadImgBtn {
    position: absolute;
    bottom: 5px;
    right: 5px;
}
</style>
